import React from 'react';
import classnames from 'classnames/bind';
import { DateTime } from 'luxon';
import { useParams } from 'react-router-dom';

import { GatewayHardwareMetricsSchema } from 'gateways/types/gateway_hardware_metrics';
import { GatewayParams } from 'types/route_params';

import { useDataGrid } from 'dashboards/hooks/useDataGrid';

import { Pill } from 'components/Pill/Pill';

import styles from './UtilizationIndicators.module.scss';

const c = classnames.bind(styles);

export function GatewayDetailHardwareUtilizationIndicators() {
  const { gatewayID } = useParams<GatewayParams>();

  const [timeRange] = React.useState(() => {
    const now = DateTime.now();
    return {
      start: now.minus({ day: 1 }).toISO(),
      stop: now.plus({ day: 1 }).toISO(),
    };
  });

  const { data } = useDataGrid({
    params: {
      dataPool: { name: 'DP_gateway_metrics' },
      // FIXME Only the latest record is needed but timeRange is required param
      timeRange,
      orderByColumn: 'timestamp',
      columns: ['timestamp', 'gateway_metric'],
      sort: 'DESC',
      timeZone: DateTime.now().zoneName,
      filters: [
        {
          column: 'gateway_id',
          operator: 'EQUALS',
          value: gatewayID,
        },
      ],
      first: 10,
    },
  });

  if (!data || data.rows.length === 0) {
    return null;
  }

  const [row] = data.rows;

  let metrics;

  try {
    metrics = GatewayHardwareMetricsSchema.parse(JSON.parse(row[1]));
  } catch {
    //
  }

  if (!metrics) {
    return null;
  }

  const high = 89;

  const hasHighCpuUsage =
    Number(metrics.cpu_utilization) > high || Number(metrics.cpu_memory) > high;
  const hasHighGpuUsage =
    Number(metrics.gpu_utilization) > high || Number(metrics.gpu_memory) > high;
  const hasHighStorageUsage = Number(metrics.disk_used_pct) > high;

  return (
    <div className={c('utilization-indicators')}>
      <div className={c('metric')}>
        <Pill
          className={c('pill')}
          intent={hasHighCpuUsage ? 'danger' : 'success'}
          icon={hasHighCpuUsage ? 'danger' : 'check'}
        />
        <span>CPU</span>
      </div>
      <div className={c('metric')}>
        <Pill
          className={c('pill')}
          intent={hasHighGpuUsage ? 'danger' : 'success'}
          icon={hasHighGpuUsage ? 'danger' : 'check'}
        />
        <span>GPU</span>
      </div>
      <div className={c('metric')}>
        <Pill
          className={c('pill')}
          intent={hasHighStorageUsage ? 'danger' : 'success'}
          icon={hasHighStorageUsage ? 'danger' : 'check'}
        />
        <span>Storage</span>
      </div>
    </div>
  );
}
